import React, { useState, useEffect } from 'react';
import MapComponent from './components/Map/MapComponent';
import StatusBar from './components/StatusBar/StatusBar';
import Legend from './components/Legend/Legend';
import Settings from './components/Settings/Settings';
import TopBar from './components/TopBar/TopBar';
import ControlPanel from './components/ControlPanel/ControlPanel';
import VesselUpload from './components/Upload/VesselUpload';
import EnvironmentalViewer from './components/Environmental/EnvironmentalViewer';
import { GeofenceManager } from './components/Geofence/GeofenceManager';
import { useRouteData } from './hooks/useRouteData';
import './App.css';

const API_BASE = import.meta.env.VITE_API_BASE || '';

// Set to true to re-enable the top bar
const SHOW_TOPBAR = false;

function App() {
    const {
        sopRoutes,
        vesselRuns,
        vessels,
        selectedVessel,
        setSelectedVessel,
        selectedSopRoute,
        setSelectedSopRoute,
        selectedRun,
        fetchRunDetails,
        fetchSopRouteDetails,
        loading,
        error,
        // Search state
        searchLoading,
        searchResultCount,
        isFiltered,
        searchRuns,
        resetRunFilters,
    } = useRouteData();

    const [status, setStatus] = useState({
        message: 'Select a vessel to view routes',
        isError: false
    });
    const [layerVisibility, setLayerVisibility] = useState({
        sopRoutes: true,
        vesselRuns: true,
        selectedRun: true
    });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        const savedDarkMode = localStorage.getItem('darkMode');
        return savedDarkMode === 'true';
    });
    const [mapStyle, setMapStyle] = useState(() => {
        const savedMapStyle = localStorage.getItem('mapStyle');
        return savedMapStyle || 'standard';
    });
    const [isPlaybackActive, setIsPlaybackActive] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackIndex, setPlaybackIndex] = useState(0);
    const [showUpload, setShowUpload] = useState(false);
    const [showEnvironmental, setShowEnvironmental] = useState(false);

    const [geofences, setGeofences] = useState(null);
    const [showGeofenceManager, setShowGeofenceManager] = useState(false);
    const [drawMode, setDrawMode] = useState({ active: false, zoneType: 'segment_zone' });
    const [geofenceRefreshKey, setGeofenceRefreshKey] = useState(0);

    // Apply dark mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark-mode');
            localStorage.setItem('darkMode', 'true');
        } else {
            document.documentElement.classList.remove('dark-mode');
            localStorage.setItem('darkMode', 'false');
        }
    }, [darkMode]);

    useEffect(() => {
        localStorage.setItem('mapStyle', mapStyle);
    }, [mapStyle]);

    useEffect(() => {
        fetchGeofences();
    }, []);

    const fetchGeofences = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/geofences`);
            if (!response.ok) throw new Error('Failed to fetch geofences');
            const data = await response.json();
            setGeofences(data);
        } catch (err) {
            console.error('[App] Error fetching geofences:', err);
            setStatus({ message: `Error loading geofences: ${err.message}`, isError: true });
        }
    };

    const handleGeofencesUpdate = (data) => {
        setGeofences(data);
    };

    const handleCloseGeofenceManager = () => {
        setShowGeofenceManager(false);
        setDrawMode({ active: false, zoneType: 'segment_zone' });
    };

    const handleDrawModeChange = (active, zoneType) => {
        setDrawMode({ active, zoneType: zoneType || 'segment_zone' });
        if (active) {
            setStatus({ message: `Drawing mode: Click on the map to create polygon points. Double-click to finish.`, isError: false });
        } else {
            setStatus({ message: 'Drawing mode disabled', isError: false });
        }
    };

    const handleGeofenceCreated = async (geojson, zoneType) => {
        const name = prompt('Enter a name for this zone:');
        if (!name) return;
        const description = prompt('Enter a description (optional):') || '';

        try {
            const response = await fetch(`${API_BASE}/api/geofences`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, zone_type: zoneType, description, geojson })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Failed to create geofence');
            }
            setStatus({ message: `✅ Zone "${name}" created successfully`, isError: false });
            await fetchGeofences();
            setGeofenceRefreshKey(prev => prev + 1);
            setDrawMode({ active: false, zoneType: 'segment_zone' });
        } catch (err) {
            console.error('[App] Error creating geofence:', err);
            setStatus({ message: `❌ Error: ${err.message}`, isError: true });
        }
    };

    useEffect(() => {
        if (drawMode.active) return;
        if (error) {
            setStatus({ message: `Error: ${error}`, isError: true });
        } else if (loading) {
            setStatus({ message: 'Loading data...', isError: false });
        } else if (selectedRun?.properties?.run_name) {
            setStatus({ message: `Viewing: ${selectedRun.properties.run_name}`, isError: false });
        } else if (selectedSopRoute?.properties?.route_name) {
            setStatus({ message: `Viewing SOP: ${selectedSopRoute.properties.route_name}`, isError: false });
        } else if (selectedVessel) {
            const runCount = vesselRuns?.length || 0;
            const suffix = isFiltered ? ' (filtered)' : '';
            setStatus({ message: `${selectedVessel} - ${runCount} run${runCount !== 1 ? 's' : ''}${suffix}`, isError: false });
        } else {
            setStatus({ message: 'Select a vessel to view routes', isError: false });
        }
    }, [error, loading, selectedVessel, vesselRuns, selectedRun, selectedSopRoute, drawMode.active, isFiltered]);

    const handleVesselSelect = (vesselName) => {
        if (!vesselName) { setSelectedVessel(null); return; }
        setSelectedVessel(vesselName);
        setSelectedSopRoute(null);
        setStatus({ message: `Loading runs for ${vesselName}...`, isError: false });
    };

    const handleRunSelect = async (runId) => {
        if (!runId) return;
        setStatus({ message: 'Loading run details...', isError: false });
        try {
            const runDetails = await fetchRunDetails(runId);
            if (runDetails?.properties?.run_name) {
                setStatus({ message: `Viewing: ${runDetails.properties.run_name}`, isError: false });
            }
        } catch (err) {
            setStatus({ message: `Error: ${err?.message || 'Failed to load run details'}`, isError: true });
        }
    };

    const handleSopRouteSelect = async (routeId) => {
        if (!routeId) { setSelectedSopRoute(null); return; }
        setStatus({ message: 'Loading SOP route...', isError: false });
        try {
            const routeDetails = await fetchSopRouteDetails(routeId);
            if (routeDetails?.properties?.route_name) {
                setStatus({ message: `Viewing SOP: ${routeDetails.properties.route_name}`, isError: false });
            }
        } catch (err) {
            setStatus({ message: `Error: ${err?.message || 'Failed to load SOP route'}`, isError: true });
        }
    };

    const toggleLayer = (layer) => {
        setLayerVisibility(prev => ({ ...prev, [layer]: !prev[layer] }));
    };

    const handleUploadComplete = ({ vessel, points }) => {
        setStatus({ message: `✅ Uploaded ${points} points for ${vessel}`, isError: false });
        window.location.reload();
    };

    const handlePlaybackActiveChange = (active) => {
        setIsPlaybackActive(active);
        if (!active) {
            setIsPlaying(false);
            setPlaybackIndex(0);
        }
    };

    const selectedSopRouteId = selectedSopRoute?.properties?.route_id || null;
    const selectedRunId = selectedRun?.properties?.run_id || null;

    return (
        <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
            {SHOW_TOPBAR && (
                <TopBar
                    onOpenUpload={() => setShowUpload(true)}
                    onOpenGeofenceManager={() => setShowGeofenceManager(true)}
                    showGeofenceManager={showGeofenceManager}
                    onCloseGeofenceManager={handleCloseGeofenceManager}
                    onGeofencesUpdate={handleGeofencesUpdate}
                    onDrawModeChange={handleDrawModeChange}
                    geofenceRefreshKey={geofenceRefreshKey}
                />
            )}

            <ControlPanel
                vessels={vessels || []}
                selectedVessel={selectedVessel || null}
                onVesselSelect={handleVesselSelect}
                sopRoutes={sopRoutes || []}
                selectedSopRoute={selectedSopRouteId}
                onSopRouteSelect={handleSopRouteSelect}
                vesselRuns={vesselRuns || []}
                selectedRun={selectedRunId}
                onRunSelect={handleRunSelect}
                onToggleLayer={toggleLayer}
                onOpenSettings={() => setSettingsOpen(true)}
                onOpenUpload={() => setShowUpload(true)}
                onOpenGeofenceManager={() => setShowGeofenceManager(true)}
                onOpenEnvironmental={() => setShowEnvironmental(true)}
                isPlaybackActive={isPlaybackActive}
                onPlaybackActiveChange={handlePlaybackActiveChange}
                isPlaying={isPlaying}
                onPlayingChange={setIsPlaying}
                playbackIndex={playbackIndex}
                onPlaybackIndexChange={setPlaybackIndex}
                layerVisibility={layerVisibility || {}}
                loading={loading || false}
                selectedRunData={selectedRun}
                selectedDatapoint={null}
                searchLoading={searchLoading}
                searchResultCount={searchResultCount}
                isFiltered={isFiltered}
                onSearchRuns={searchRuns}
                onResetRunFilters={resetRunFilters}
                geofences={geofences}
            />

            <MapComponent
                sopRoutes={layerVisibility?.sopRoutes ? (sopRoutes || []) : []}
                vesselRuns={layerVisibility?.vesselRuns ? (vesselRuns || []) : []}
                selectedSopRoute={layerVisibility?.selectedRun ? selectedSopRoute : null}
                selectedRun={layerVisibility?.selectedRun ? selectedRun : null}
                onRunSelect={handleRunSelect}
                mapStyle={mapStyle || 'standard'}
                isPlaybackActive={isPlaybackActive}
                onPlaybackActiveChange={handlePlaybackActiveChange}
                geofences={geofences}
                drawMode={drawMode}
                onGeofenceCreated={handleGeofenceCreated}
                playbackIndex={playbackIndex}
                isPlaying={isPlaying}
            />

            <Legend />
            <StatusBar message={status?.message || ''} isError={status?.isError || false} />
            <Settings
                isOpen={settingsOpen || false}
                onClose={() => setSettingsOpen(false)}
                darkMode={darkMode || false}
                onToggleDarkMode={setDarkMode}
                mapStyle={mapStyle || 'standard'}
                onChangeMapStyle={setMapStyle}
            />

            {showUpload && (
                <VesselUpload
                    onClose={() => setShowUpload(false)}
                    onUploadComplete={handleUploadComplete}
                />
            )}

            {showEnvironmental && (
                <div className="env-overlay">
                    <div className="env-overlay-backdrop" onClick={() => setShowEnvironmental(false)} />
                    <div className="env-overlay-panel">
                        <button
                            className="env-overlay-close"
                            onClick={() => setShowEnvironmental(false)}
                            title="Close"
                        >
                            ✕
                        </button>
                        <EnvironmentalViewer />
                    </div>
                </div>
            )}

            {/* GeofenceManager rendered directly so it works when TopBar is hidden */}
            {showGeofenceManager && (
                <GeofenceManager
                    isOpen={showGeofenceManager}
                    onClose={handleCloseGeofenceManager}
                    onGeofencesLoad={handleGeofencesUpdate}
                    onDrawModeChange={handleDrawModeChange}
                    refreshKey={geofenceRefreshKey}
                />
            )}
        </div>
    );
}

export default App;